import React from 'react';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FaCookieBite, FaShieldHalved, FaLock, FaCircleInfo, FaArrowRight } from 'react-icons/fa6';
import Seo from '@/components/seo';

export default function CookiesPage() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Política de Cookies',
        item: 'https://aboutcar.com.br/cookies'
      }
    ]
  };

  return (
    <>
      <Seo
        title="Política de Cookies | AboutCar"
        description="Entenda como a AboutCar utiliza cookies para garantir o funcionamento seguro da plataforma, melhorar o desempenho e otimizar sua experiência."
        keywords="politica de cookies, cookies sobre carros, preferencias de navegacao, privacidade cookies aboutcar"
        schema={breadcrumbSchema}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2">
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-3 fs-7">
                Privacidade & Preferências
              </span>
              <h1 className="display-4 fw-extrabold text-dark mb-3">
                Política de <span className="text-primary">Cookies</span>
              </h1>
              <p className="lead text-secondary mb-4">
                Entenda como utilizamos cookies para personalizar sua experiência, garantir a segurança das suas sessões e aprimorar nossas soluções veiculares.
              </p>

              <div className="d-flex flex-wrap gap-3">
                <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                  <FaCookieBite className="text-primary" />
                  <span className="small fw-semibold text-dark">Navegação Personalizada</span>
                </div>
                <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                  <FaShieldHalved className="text-success" />
                  <span className="small fw-semibold text-dark">Sessões Protegidas</span>
                </div>
              </div>
            </Col>

            <Col lg={4} className="text-center text-lg-end">
              <div className="p-4 bg-white rounded-4 shadow-sm border border-light-subtle text-start d-inline-block w-100 max-w-350">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="bg-primary-subtle text-primary p-3 rounded-circle">
                    <FaLock className="fs-3" />
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-0">LGPD & Privacidade</h6>
                    <span className="small text-muted">Termo Integrado</span>
                  </div>
                </div>
                <p className="small text-secondary mb-3">Esta Política de Cookies faz parte da nossa Política de Privacidade geral.</p>
                <a href="/privacidade" className="btn btn-outline-primary rounded-pill btn-sm fw-bold w-100 d-flex align-items-center justify-content-center gap-2">
                  Ver Política de Privacidade <FaArrowRight />
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Content Section */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5 mb-4">
              <div className="d-flex flex-column gap-4 text-secondary leading-relaxed">

                {/* 1. Definições */}
                <section id="section_1">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>01</span>
                    O que são Cookies?
                  </h3>
                  <p className="ms-md-4 mb-3">
                    <strong>Cookies</strong> são pequenos arquivos de texto armazenados no seu computador ou dispositivo móvel pelo seu navegador ao acessar nossos sites. Eles desempenham papel fundamental para:
                  </p>
                  <ul className="ms-md-4 d-flex flex-column gap-2 mb-0">
                    <li>Reconhecer se você já visitou a AboutCar anteriormente;</li>
                    <li>Manter sua sessão de usuário ativa e segura durante a consulta;</li>
                    <li>Mentar estatísticas anônimas de acesso para melhorias contínuas.</li>
                  </ul>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 2. Sobre a política */}
                <section id="section_2">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>02</span>
                    Sobre esta Política
                  </h3>
                  <p className="ms-md-4 mb-0">
                    Nesta Política de Cookies, qualquer menção a nossos sites refere-se a plataformas, sistemas ou aplicativos móveis operados pela <strong>ABOUTCAR SOLUÇÕES</strong>. Esta diretriz faz parte integrante da nossa <a href="/privacidade" className="fw-bold text-primary text-decoration-none">Política de Privacidade</a>.
                  </p>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 3. Nosso uso de cookies */}
                <section id="section_3">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>03</span>
                    Como Utilizamos os Cookies?
                  </h3>
                  <p className="ms-md-4 mb-2">
                    Recolhemos informações automaticamente ao visitar nossos sites por meio dos cookies. Você pode alterar as configurações do seu navegador a qualquer momento para desativar ou apagar os cookies.
                  </p>
                  <div className="ms-md-4 bg-light p-3 rounded-3 border border-light-subtle d-flex align-items-center gap-3">
                    <FaCircleInfo className="fs-3 text-primary flex-shrink-0" />
                    <span className="small text-secondary">
                      Ao desativar os cookies essenciais, certas funcionalidades e áreas de consulta segura podem não funcionar perfeitamente.
                    </span>
                  </div>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 4. Tipos de cookies */}
                <section id="section_4">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>04</span>
                    Categorias de Cookies Utilizados
                  </h3>
                  <p className="ms-md-4 mb-3">
                    Abaixo explicamos os principais tipos de cookies armazenados durante sua experiência em nosso portal:
                  </p>

                  <div className="ms-md-4 table-responsive rounded-3 border overflow-hidden">
                    <Table hover align-middle className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="p-3 text-secondary border-0" style={{ width: '25%' }}>Categoria</th>
                          <th className="p-3 text-secondary border-0" style={{ width: '75%' }}>Finalidade e Exemplo</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-3 fw-bold text-dark">Preferências</td>
                          <td className="p-3 text-secondary">Permitem lembrar informações que alteram o comportamento ou exibição do site, como idioma e preferências regionais.</td>
                        </tr>
                        <tr>
                          <td className="p-3 fw-bold text-dark">Segurança</td>
                          <td className="p-3 text-secondary">Autenticam usuários, previnem utilização fraudulenta de credenciais e protegem dados contra terceiros não autorizados.</td>
                        </tr>
                        <tr>
                          <td className="p-3 fw-bold text-dark">Processos</td>
                          <td className="p-3 text-secondary">Garantem o funcionamento adequado do site e o acesso a páginas seguras e checkout de consultas.</td>
                        </tr>
                        <tr>
                          <td className="p-3 fw-bold text-dark">Publicidade</td>
                          <td className="p-3 text-secondary">Tornam anúncios mais relevantes para os usuários e medem a eficiência de campanhas promocionais.</td>
                        </tr>
                        <tr>
                          <td className="p-3 fw-bold text-dark">Estado de Sessão</td>
                          <td className="p-3 text-secondary">Coletam métricas de navegação anônimas para identificar as páginas mais visitadas e solucionar eventuais falhas.</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 5. Revisões */}
                <section id="section_5">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>05</span>
                    Revisões à Política
                  </h3>
                  <p className="ms-md-4 mb-0">
                    Este documento é válido a partir de 01/02/2021. Reservamo-nos o direito de alterar esta Política de Cookies periodicamente. Quaisquer modificações entram em vigor imediatamente após a sua publicação oficial nesta página.
                  </p>
                </section>

              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
